import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  MenuItem,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";

const fin = {
  primary: "#009db9",
  primaryDark: "#007a8a",
  primarySoft: "#e6f6f9",
  text: "#1f2933",
  muted: "#6b7280",
  border: "#e5e7eb",
  surface: "#f7f9fb",
  success: "#2e7d32",
  successSoft: "#e8f5e9",
  danger: "#d32f2f",
  dangerSoft: "#ffebee",
  warning: "#b26a00",
  warningSoft: "#fff4e5",
  radius: "16px",
  shadow: "0 4px 20px rgba(16, 24, 40, 0.06)",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#fff",
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: fin.primary },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: fin.primary,
    },
    "&.Mui-disabled": { backgroundColor: "#f3f5f7" },
  },
  "& label.Mui-focused": { color: fin.primary },
};

const headCellSx = {
  fontWeight: 700,
  color: fin.primaryDark,
  backgroundColor: fin.primarySoft,
  fontSize: "0.8rem",
  py: 1.5,
  borderBottom: `1px solid ${fin.border}`,
};

const bodyCellSx = {
  color: fin.text,
  py: 1.75,
  fontSize: "0.875rem",
  borderBottom: `1px solid ${fin.border}`,
};

const mobileCardSx = {
  border: `1px solid ${fin.border}`,
  borderRadius: "12px",
  p: 2,
  backgroundColor: "#fff",
};

const selectMenuProps = { PaperProps: { sx: { maxHeight: 320 } } };

const formatKsh = (value) =>
  `KSh ${(Number(value) || 0).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  })}`;

const isPaid = (status) => status === "Yes";

const chipTones = {
  success: { bg: fin.successSoft, color: fin.success },
  danger: { bg: fin.dangerSoft, color: fin.danger },
  warning: { bg: fin.warningSoft, color: fin.warning },
  info: { bg: fin.primarySoft, color: fin.primaryDark },
  neutral: { bg: "#eef1f4", color: "#4b5563" },
};

const PillChip = ({ label, tone = "neutral" }) => {
  const t = chipTones[tone] || chipTones.neutral;
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        fontWeight: 700,
        borderRadius: "8px",
        backgroundColor: t.bg,
        color: t.color,
      }}
    />
  );
};

const EmptyState = ({ icon: Icon = InboxOutlinedIcon, title, message, action }) => (
  <Box sx={{ textAlign: "center", py: { xs: 5, md: 7 }, px: 2 }}>
    <Box
      sx={{
        width: 64,
        height: 64,
        mx: "auto",
        mb: 2,
        borderRadius: "50%",
        backgroundColor: fin.primarySoft,
        color: fin.primary,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Icon sx={{ fontSize: 32 }} />
    </Box>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: fin.text }}>
      {title}
    </Typography>
    {message && (
      <Typography
        variant="body2"
        sx={{ color: fin.muted, mt: 0.5, maxWidth: 360, mx: "auto" }}
      >
        {message}
      </Typography>
    )}
    {action && <Box sx={{ mt: 2.5 }}>{action}</Box>}
  </Box>
);

const ListSkeleton = ({ rows = 5 }) => (
  <Box
    role="status"
    aria-label="Loading"
    sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
  >
    {Array.from({ length: rows }).map((_, i) => (
      <Skeleton key={i} variant="rounded" height={56} animation="wave" />
    ))}
  </Box>
);

const SectionCard = ({ title, action, children }) => (
  <Card
    sx={{
      mb: { xs: 2.5, md: 4 },
      borderRadius: fin.radius,
      border: `1px solid ${fin.border}`,
      backgroundColor: "#fff",
      boxShadow: fin.shadow,
      overflow: "visible",
    }}
  >
    <CardContent
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        "&:last-child": { pb: { xs: 2, sm: 3, md: 4 } },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          gap: 2,
          mb: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: fin.text,
            pl: 1.5,
            borderLeft: `4px solid ${fin.primary}`,
            lineHeight: 1.3,
          }}
        >
          {title}
        </Typography>
        {action}
      </Box>
      {children}
    </CardContent>
  </Card>
);

const EarningsHistory = ({
  historyLoading,
  filteredData,
  uniqueEarningNames,
  filter,
  setFilter,
  rowsPerPage,
  totalElements,
  page,
  handleChangePage,
  handleChangeRowsPerPage,
  handleOpenModal,
}) => {
  const statusChip = (status) => (
    <PillChip
      label={isPaid(status) ? "Paid" : "Not paid"}
      tone={isPaid(status) ? "success" : "danger"}
    />
  );

  const renderActions = (e, withLabels) => {
    const editDisabled = e.bankAccount === "No Bank Account";
    if (withLabels) {
      return (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            onClick={() => handleOpenModal(e, "Edit")}
            disabled={editDisabled}
            startIcon={<EditOutlinedIcon fontSize="small" />}
            variant="outlined"
            size="small"
            sx={{
              flex: 1,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              color: fin.primary,
              borderColor: fin.primary,
              "&:hover": {
                borderColor: fin.primaryDark,
                backgroundColor: fin.primarySoft,
              },
            }}
          >
            Edit
          </Button>
          <Button
            onClick={() => handleOpenModal(e, "Delete")}
            startIcon={<DeleteOutlineIcon fontSize="small" />}
            variant="outlined"
            size="small"
            sx={{
              flex: 1,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              color: fin.danger,
              borderColor: fin.danger,
              "&:hover": {
                borderColor: fin.danger,
                backgroundColor: fin.dangerSoft,
              },
            }}
          >
            Delete
          </Button>
        </Box>
      );
    }
    return (
      <Box sx={{ display: "flex", gap: 0.5 }}>
        <Tooltip title={editDisabled ? "No bank account linked" : "Edit"}>
          <span>
            <IconButton
              onClick={() => handleOpenModal(e, "Edit")}
              disabled={editDisabled}
              size="small"
              aria-label={`Edit earning for ${e.name}`}
              sx={{
                color: fin.primary,
                "&:hover": { backgroundColor: fin.primarySoft },
              }}
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton
            onClick={() => handleOpenModal(e, "Delete")}
            size="small"
            aria-label={`Delete earning for ${e.name}`}
            sx={{
              color: fin.danger,
              "&:hover": { backgroundColor: fin.dangerSoft },
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  return (
    <SectionCard
      title="Earnings history"
      action={
        <TextField
          select
          size="small"
          label="Filter by user"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          sx={{ ...fieldSx, minWidth: { sm: 200 } }}
          SelectProps={{ MenuProps: selectMenuProps }}
        >
          <MenuItem value="All">All users</MenuItem>
          {uniqueEarningNames.map((name) => (
            <MenuItem key={name} value={name}>
              {name}
            </MenuItem>
          ))}
        </TextField>
      }
    >
      {historyLoading ? (
        <ListSkeleton rows={rowsPerPage > 5 ? 6 : rowsPerPage} />
      ) : filteredData.length === 0 ? (
        <EmptyState
          icon={ReceiptLongOutlinedIcon}
          title="No earnings found"
          message={
            filter === "All"
              ? "Earnings you save will show up here."
              : "This user has no earnings on the current page. Try another user or page."
          }
        />
      ) : (
        <>
          {/* Desktop table */}
          <Box
            sx={{
              display: { xs: "none", lg: "block" },
              overflowX: "auto",
              border: `1px solid ${fin.border}`,
              borderRadius: "12px",
            }}
          >
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  {[
                    "Name",
                    "Payment method",
                    "Gross pay",
                    "Payment date",
                    "Status",
                    "Actions",
                  ].map((h) => (
                    <TableCell key={h} sx={headCellSx}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.map((e) => (
                  <TableRow
                    key={e.id}
                    hover
                    sx={{ "&:last-child td": { borderBottom: 0 } }}
                  >
                    <TableCell sx={bodyCellSx}>
                      <Typography sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                        {e.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: fin.muted }}>
                        {e.userType}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, wordBreak: "break-word" }}>
                      {e.bankAccount}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Typography sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                        {formatKsh(e.grossPay)}
                      </Typography>
                      {Number(e.bonus) > 0 && (
                        <Typography variant="caption" sx={{ color: fin.muted }}>
                          + {formatKsh(e.bonus)} bonus
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>{e.paymentDate}</TableCell>
                    <TableCell sx={bodyCellSx}>
                      {statusChip(e.paymentStatus)}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      {renderActions(e, false)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {/* Mobile / tablet cards */}
          <Box
            sx={{
              display: { xs: "flex", lg: "none" },
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            {filteredData.map((e) => (
              <Box key={e.id} sx={mobileCardSx}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, color: fin.text }}>
                      {e.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: fin.muted, wordBreak: "break-word" }}
                    >
                      {e.bankAccount}
                    </Typography>
                  </Box>
                  {statusChip(e.paymentStatus)}
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                    gap: 1,
                    p: 1.5,
                    mb: 1.5,
                    borderRadius: "10px",
                    backgroundColor: fin.surface,
                  }}
                >
                  <Box>
                    <Typography variant="caption" sx={{ color: fin.muted }}>
                      Gross pay
                    </Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                      {formatKsh(e.grossPay)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: fin.muted }}>
                      Bonus
                    </Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                      {formatKsh(e.bonus)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: fin.muted }}>
                      Payment date
                    </Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                      {e.paymentDate}
                    </Typography>
                  </Box>
                </Box>
                {renderActions(e, true)}
              </Box>
            ))}
          </Box>
        </>
      )}

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        component="div"
        count={totalElements}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          mt: 2,
          color: fin.text,
          "& .MuiTablePagination-toolbar": {
            flexWrap: "wrap",
            justifyContent: { xs: "center", sm: "flex-end" },
            px: 0,
          },
          "& .MuiTablePagination-spacer": { display: { xs: "none", sm: "block" } },
        }}
        labelRowsPerPage="Rows per page:"
        nextIconButtonProps={{ "aria-label": "Next page" }}
        backIconButtonProps={{ "aria-label": "Previous page" }}
        showFirstButton
        showLastButton
      />
    </SectionCard>
  );
};

export default EarningsHistory;